/**
 * ModelFormDialog — shadcn Dialog wrapping the shared ModelForm.
 *
 * Used by ModelsListPage (create), ModelDetailPage (edit) and the staff
 * team configuration page (both, with a staff scope) so the form never
 * needs its own route. onSaved fires after the create/update mutation
 * resolves; callers decide what to do next (navigate, close, toast).
 */

import { ModelForm } from '@/components/models/ModelForm'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Model, ModelScope } from '@/hooks/useModels'

interface ModelFormDialogProps {
  mode: 'create' | 'edit'
  model?: Model
  scope: ModelScope
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved?: (model: Model) => void
}

export function ModelFormDialog({
  mode,
  model,
  scope,
  open,
  onOpenChange,
  onSaved,
}: ModelFormDialogProps): React.JSX.Element {
  function handleSaved(saved: Model): void {
    onOpenChange(false)
    onSaved?.(saved)
  }

  function handleCancel(): void {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New model' : 'Edit model'}</DialogTitle>
        </DialogHeader>
        <ModelForm
          mode={mode}
          initial={mode === 'edit' ? model : undefined}
          scope={scope}
          onSaved={handleSaved}
          onCancel={handleCancel}
        />
      </DialogContent>
    </Dialog>
  )
}
